import React, { lazy, Suspense, useEffect, useState } from "react";
import "./_App.scss";
import { Routes, Route, BrowserRouter, useNavigate, useLocation } from "react-router-dom";
import UnAuthGuard from "./guard/UnAuthGuard";
import AuthGuard from "./guard/AuthGuards";

import { ThemeProvider } from "@mui/material/styles";
import lcTheme from "./theme";
import CssBaseline from "@mui/material/CssBaseline";

// VIEWS
import Login from "./views/Auth/Login";
import Homepage from "./views/Homepage/Homepage";
import Citizenonboarding from "./views/Public/Citizenonboarding";
import Home from "./views/LandingPage/Home";
import Resetpassword from "./views/Auth/Resetpassword";
import OneAlert from "./views/Public/OneAlert";
import Myprofile from "./views/Public/Myprofile";
import Apicall from "./libs/Apicall";
import PolitiqueConfidentialite from "./views/PolitiqueConfidentialite/PolitiqueConfidentialite";
import Support from "./views/Support/Support";
import { ScrollToTop } from "./components/Layout/ScrollTop/ScrollToTop";
import ChargementEcran from "./components/ChargementEcran";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";

/*
 * Chargement différé des modules d'administration et du Super Admin.
 *
 * Un citoyen qui consulte une alerte téléchargeait tout l'admin municipal
 * et tout l'espace Cognitif — du code qu'il ne verra jamais. Les écrans
 * citoyens (connexion, accueil, alerte, profil, inscription) restent en
 * chargement immédiat : ce sont eux qui doivent s'afficher vite.
 */
const Alertlist = lazy(() => import("./views/Alert/Alertlist"));
const Calendarlist = lazy(() => import("./views/Calendar/Calendarlist"));
const Citizenslist = lazy(() => import("./views/Citizens/Citizenslist"));
const Collectionmaterials = lazy(() => import("./views/Collections/Collectionmaterials"));
const Collectionrules = lazy(() => import("./views/Collections/Collectionrules"));
const Collectionzones = lazy(() => import("./views/Collections/Collectionzones"));
const DashboardSelector = lazy(() => import("./views/Dashboard/DashboardSelector"));
const Directoryoffice = lazy(() => import("./views/Directory/Directoryoffice"));
const Directoryplaceform = lazy(() => import("./views/Directory/Directoryplaceform"));
const Directoryplaceslist = lazy(() => import("./views/Directory/Directoryplaceslist"));
const Editcitizen = lazy(() => import("./views/Citizens/Editcitizen"));
const Assistantform = lazy(() => import("./views/Assistant/Assistantform"));
const Eventform = lazy(() => import("./views/Calendar/Eventform"));
const Eventtypes = lazy(() => import("./views/Calendar/Eventtypes"));
const Municipalityform = lazy(() => import("./views/Municipality/Municipalityform"));
const Mymunicipality = lazy(() => import("./views/Mymunicipality/Mymunicipality"));
const Newalert = lazy(() => import("./views/Alert/Newalert"));
const Newcitizen = lazy(() => import("./views/Citizens/Newcitizen"));
const Newsarticle = lazy(() => import("./views/News/Newsarticle"));
const Newscategories = lazy(() => import("./views/News/Newscategories"));
const Newslist = lazy(() => import("./views/News/Newslist"));
const Permitformbuilder = lazy(() => import("./views/Permits/Permitformbuilder"));
const Permitformcategories = lazy(() => import("./views/Permits/Permitformcategories"));
const Permitformslist = lazy(() => import("./views/Permits/Permitformslist"));
const Permitrequestslist = lazy(() => import("./views/Permits/Permitrequestslist"));
const Signalementcategories = lazy(() => import("./views/Signalements/Signalementcategories"));
const Signalementslist = lazy(() => import("./views/Signalements/Signalementslist"));
const SuperAdminApp = lazy(() => import("./views/SuperAdmin/SuperAdminApp"));
const Surveyform = lazy(() => import("./views/Surveys/Surveyform"));
const Surveyslist = lazy(() => import("./views/Surveys/Surveyslist"));
const TwoFactorSetup = lazy(() => import("./views/Auth/TwoFactorSetup"));
const TwoFactorVerify = lazy(() => import("./views/Auth/TwoFactorVerify"));
const Security = lazy(() => import("./views/Account/Security"));


// Sous-domaine dédié à l'espace Super Admin (Cognitif) — voir
// docs/inventaire-multi-villes.md Q1. Comparé au premier segment de l'hôte
// (comme ConfigController::getmunicipalityconfig côté backend), pour
// fonctionner identiquement en prod (superadmin.liencitoyen.com) et en
// local (superadmin.localhost:3000).
const SUPERADMIN_SUBDOMAIN = 'superadmin';
const isSuperAdminHost = (host: string): boolean =>
    host.split('.')[0] === SUPERADMIN_SUBDOMAIN;

export interface municipalityAlertTypeInterface {
    id: number;
    key: string;
    name: string;
    icon_asset: string | null;
    icon_url: string | null;
}

export interface currentMunicipalityInterface {
    default_city_name: string;
    defaultlat: number;
    defaultlng: number;
    id: number;
    logo: string;
    logo2: string;
    splash_background: string;
    can_use_voice_msg: boolean;
    alert_types: municipalityAlertTypeInterface[];
}

// Pages de PLATEFORME (pas propres à une ville) : doivent rester
// accessibles même sur liencitoyen.com, où config/get-municipality échoue
// toujours par conception (aucune ville associée à ce domaine).
const PAGES_SANS_VILLE = ["/support", "/politique-confidentialite"];

function MunicipalityHandler() {
    const navigate = useNavigate();
    const location = useLocation();
    const [currentMunicipality, setCurrentMunicipality] =
        useState<currentMunicipalityInterface | null>(null);
    const [hasError, setHasError] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const api = new Apicall();
        api.call("POST", "config/get-municipality", {
            domain: window.location.host,
        }).then((response) => {
            if (response.status !== "OK") {
                setHasError(true);
                if (!PAGES_SANS_VILLE.includes(window.location.pathname)) {
                    navigate("/");
                }
            } else {
                setCurrentMunicipality(response.data);
            }
            setLoaded(true);
        });
    }, [navigate]);

    if (!loaded) {
        return null;
    }

    if (hasError && !PAGES_SANS_VILLE.includes(location.pathname)) {
        return <Home />;
    }

    // Si tout est en ordre, rendu des enfants (qui sont les routes dans ce cas)
    return (
        // Suspense : les modules différés ci-dessus arrivent en un second
        // téléchargement, déclenché par la navigation vers leur route.
        <ChunkErrorBoundary>
        <Suspense fallback={<ChargementEcran />}>
        <Routes>
            <Route
                path="/"
                element={
                    currentMunicipality ? (
                        <UnAuthGuard
                            trytogetuser={true}
                            component={
                                <Homepage municipality={currentMunicipality} />
                            }
                        />
                    ) : (
                        <Home />
                    )
                }
            />
            {/*Pages de plateforme : accessibles sans ville résolue (voir PAGES_SANS_VILLE)*/}
            <Route
                path="/politique-confidentialite"
                element={<UnAuthGuard component={<PolitiqueConfidentialite />} />}
            ></Route>
            <Route
                path="/support"
                element={<UnAuthGuard component={<Support />} />}
            ></Route>
            {currentMunicipality && (
                <>
                    {/*NOT PASSWORD PROTECTED*/}
                    <Route
                        path="/login"
                        element={
                            <UnAuthGuard
                                component={
                                    <Login municipality={currentMunicipality} />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/nouveau-mot-de-passe"
                        element={
                            <UnAuthGuard
                                component={
                                    <Resetpassword
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/s-enregistrer"
                        element={
                            <UnAuthGuard
                                component={
                                    <Citizenonboarding
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/alerte/:slug"
                        element={
                            <UnAuthGuard
                                component={
                                    <OneAlert
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    {/*PASSWORD PROTECTED*/}
                    <Route
                        path="/dashboard"
                        element={
                            <AuthGuard
                                roles={["ALL"]}
                                component={
                                    <DashboardSelector
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/securite"
                        element={
                            <AuthGuard
                                roles={[
                                    "CITYADMIN",
                                    "CITYREDACTOR",
                                    "CITYADMINRESTRICTED",
                                ]}
                                component={<Security />}
                            />
                        }
                    ></Route>
                    {/* skipTwoFactorCheck : ces deux écrans sont le moyen de
                        se conformer au 2FA, pas quelque chose qu'il faut
                        déjà avoir fait pour les atteindre (voir AuthGuard). */}
                    <Route
                        path="/2fa/configuration"
                        element={
                            <AuthGuard
                                roles={[
                                    "CITYADMIN",
                                    "CITYREDACTOR",
                                    "CITYADMINRESTRICTED",
                                ]}
                                skipTwoFactorCheck
                                component={<TwoFactorSetup />}
                            />
                        }
                    ></Route>
                    <Route
                        path="/2fa/verification"
                        element={
                            <AuthGuard
                                roles={[
                                    "CITYADMIN",
                                    "CITYREDACTOR",
                                    "CITYADMINRESTRICTED",
                                ]}
                                skipTwoFactorCheck
                                component={<TwoFactorVerify />}
                            />
                        }
                    ></Route>
                    <Route
                        path="/nouvelle-alerte"
                        element={
                            <AuthGuard
                                roles={[
                                    "CITYADMIN",
                                    "CITYREDACTOR",
                                    "CITYADMINRESTRICTED",
                                ]}
                                component={
                                    <Newalert
                                        mode={"add"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/alertes"
                        element={
                            <AuthGuard
                                roles={[
                                    "CITYADMIN",
                                    "CITYREDACTOR",
                                    "CITYADMINRESTRICTED",
                                ]}
                                component={
                                    <Alertlist
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/alertes/modifier/:id"
                        element={
                            <AuthGuard
                                roles={[
                                    "CITYADMIN",
                                    "CITYREDACTOR",
                                    "CITYADMINRESTRICTED",
                                ]}
                                component={
                                    <Newalert
                                        mode={"edit"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/ma-ville"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Mymunicipality
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/ma-ville/:adminid"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Mymunicipality
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/citoyens"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Citizenslist
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/nouveau-citoyen"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Newcitizen
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/modifier-citoyen/:id"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Editcitizen
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/mon-profil"
                        element={
                            <AuthGuard
                                roles={["ALL"]}
                                component={
                                    <Myprofile
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/actualites"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Newslist
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/actualites/categories"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Newscategories
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/nouvelle-actualite"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Newsarticle
                                        mode={"add"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/actualites/modifier/:id"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Newsarticle
                                        mode={"edit"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/calendrier"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Calendarlist
                                        variant={"upcoming"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/calendrier/passes"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Calendarlist
                                        variant={"past"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/calendrier/types"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Eventtypes
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/nouvel-evenement"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Eventform
                                        mode={"add"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/calendrier/modifier/:id"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Eventform
                                        mode={"edit"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/sondages"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Surveyslist
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/nouveau-sondage"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Surveyform
                                        mode={"add"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/sondages/modifier/:id"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Surveyform
                                        mode={"edit"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/ma-municipalite"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Municipalityform
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/assistant"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Assistantform
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/signalements"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Signalementslist
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/signalements/categories"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Signalementcategories
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/collectes/regles"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Collectionrules
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/collectes/zones"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Collectionzones
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/collectes/matieres"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Collectionmaterials
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/permis/formulaires"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Permitformslist
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/permis/formulaires/categories"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Permitformcategories
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/permis/formulaires/nouveau"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Permitformbuilder
                                        mode={"add"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/permis/formulaires/modifier/:id"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Permitformbuilder
                                        mode={"edit"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/permis/demandes"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Permitrequestslist
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/repertoires/hotel-de-ville"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Directoryoffice
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    {/* Un type par ville/formulaire pour chaque clé de
                        DirectoryKind, résolue depuis l'URL (:kindKey) plutôt
                        qu'énumérée ici — un type ajouté depuis le Super Admin
                        obtient sa route sans déploiement. */}
                    <Route
                        path="/repertoires/:kindKey"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Directoryplaceslist
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/repertoires/:kindKey/nouveau"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Directoryplaceform
                                        mode={"add"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                    <Route
                        path="/repertoires/:kindKey/modifier/:id"
                        element={
                            <AuthGuard
                                roles={["CITYADMIN", "CITYREDACTOR"]}
                                component={
                                    <Directoryplaceform
                                        mode={"edit"}
                                        municipality={currentMunicipality}
                                    />
                                }
                            />
                        }
                    ></Route>
                </>
            )}
        </Routes>
        </Suspense>
        </ChunkErrorBoundary>
    );
}

function App() {
    const isSuperAdmin = isSuperAdminHost(window.location.host);

    // Tout l'espace Super Admin (login compris) masque le bouton CookieYes ;
    // côté ville, c'est AuthGuard qui s'en charge selon le rôle.
    useEffect(() => {
        document.body.classList.toggle("superadmin-host", isSuperAdmin);
    }, [isSuperAdmin]);

    return (
        <ThemeProvider theme={lcTheme}>
            <CssBaseline />
            <BrowserRouter>
                {isSuperAdmin ? <SuperAdminApp /> : <MunicipalityHandler />}
                <ScrollToTop />
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
